import {get} from './object';

/**
 * @name debounce
 * @desc Debounces a function so its called only after it stops being called; be sure to use event.persist() for accessing event properties in an asynchronous way in react
 * @param {Function} func function to be debounced
 * @param {Number} wait waiting time in milliseconds for debouncing effect
 * @param {Boolean} immediate trigger the function on the leading edge, instead of the trailing.
 * @return {Function}
 * @memberof utils
 * @example
 * const myEfficientFn = debounce(function() {
 *  // All the taxing stuff you do
 * }, 250);
 * window.addEventListener('resize', myEfficientFn);
 */
export function debounce(func, wait, immediate) {
  let timeout;
  return function () {
    let context = this, args = arguments;
    let later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

/**
 * @name callFunctionWithObjPathValues
 * @private
 * @desc Checks if a value exists at the the given path in the passed in object and runs the given function on that value
 * @param {Object} {{obj: object, path: string, paths: array, fn: function}}
 * @returns {*|null}
 **/
export function callFunctionWithObjPathValues({obj, path, paths, fn}) {
  const params = [];
  if (!paths) paths = [path];

  paths.forEach(path => {
    const value = get(obj, path);
    if (value) params.push(value);
  });

  if (params.length) return fn(...params);
  return null;
}
